<script setup lang="ts">
/**
 * UI-kit component
 * Category: Inputs
 * Name: 'AppInput'
 * FIGMA: https://www.figma.com/design/oRsJUOAucnhxx5aeqy4TRO/APP-admin?node-id=5382-72137
 */
import { useId, useSlots } from 'vue'

import type { AppInputProps } from '../types/interfaces'

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

import { AppText } from '@shared/ui/typography'
import { IconContainer } from '@shared/ui/other'

import { APP_INPUT_STATE } from '../types/types'
import { useAppInput } from '../composables/use-app-input.hook'

defineOptions({
  name: 'AppInput',
})

const props = withDefaults(defineProps<AppInputProps>(), {
  type: 'text',
  state: 'static',
  label: undefined,
  placeholder: '',
  message: undefined,
  name: undefined,
  autocomplete: 'off',
  disabled: false,
  prependIcon: undefined,
  appendIcon: undefined,
  min: undefined,
  max: undefined,
  maxLength: undefined,
  list: undefined,
})

const emit = defineEmits<{
  'keyup:enter': [value: string | number]
}>()

const slots = useSlots()
const inputId = useId()

const model = defineModel<string | number>({ default: '' })

const {
  isDisabled,
  inputClasses,
  messageColor,
  messageId,
  handlers,
} = useAppInput({
  model,
  props,
  slots,
  inputId,
  onKeyupEnter: (value: string | number) => emit('keyup:enter', value),
})
</script>

<template>
  <div
    class="app-input"
    :class="inputClasses"
  >
    <label
      v-if="label"
      class="app-input__label"
      :for="inputId"
    >
      {{ label }}
    </label>

    <div class="app-input__wrapper">
      <slot name="prepend" />

      <slot name="prepend-icon">
        <IconContainer v-if="prependIcon">
          <FontAwesomeIcon :icon="prependIcon" />
        </IconContainer>
      </slot>

      <input
        :id="inputId"
        v-model="model"
        class="app-input__field"
        :type="type"
        :name="name"
        :placeholder="placeholder"
        :disabled="isDisabled"
        :autocomplete="autocomplete"
        :min="min"
        :max="max"
        :maxlength="maxLength"
        :list="list"
        :aria-invalid="state === APP_INPUT_STATE.Error || undefined"
        :aria-describedby="messageId"
        @input="handlers.handleInput"
        @change="handlers.handleChange"
        @keyup.enter="handlers.handleKeyupEnter"
      >

      <slot name="append-icon">
        <IconContainer v-if="appendIcon">
          <FontAwesomeIcon :icon="appendIcon" />
        </IconContainer>
      </slot>

      <slot name="append" />
    </div>

    <AppText
      v-if="message"
      :id="messageId"
      name="body2"
      :color="messageColor"
      class="app-input__message"
    >
      {{ message }}
    </AppText>
  </div>
</template>

<style scoped lang="scss" src="../styles/_app-input.scss" />
